import { getSortedData } from '@/contexts/data-context/get-sorted-data';
import { LoadingSection } from '@/design-system/loading/loading';
import { isModulrAccount } from '@/utils/accounts/is-modulr-account';
import * as VisuallyHiddenPrimitive from '@radix-ui/react-visually-hidden';
import { Currency, EvidenceStatus, LegalEntityCountry, PayrollStatus } from '@seapoint/server/enums';
import * as Sentry from '@sentry/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ComponentProps, useRef, useState } from 'react';
import { toast } from 'sonner';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import { usePayeeCheck } from '../../../api/use-payee-check';
import { useData } from '../../../contexts/data-context/use-data';
import { FileDropZone } from '../../../design-system/file-drop-zone/file-drop-zone';
import {
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalDescription,
  ModalTitle,
} from '../../../design-system/modal/modal';
import { Stack } from '../../../design-system/stack/stack';
import {
  AccountBankingTypeView,
  BeneficiaryView,
  EmployeeView,
  PayrollEvidenceLineItemView,
  PayrollEvidenceView,
  PayrollView,
} from '../../../types/entity-types';
import { exhaustiveCheck } from '../../../utils/exhaustive-check';
import { getSwrFetcher } from '../../../utils/get-swr-fetcher';
import { isVopCheckRequired } from '../../../utils/payee-check/is-vop-check-required';
import { isVopPayeeCheck } from '../../../utils/payee-check/is-vop-payee-check';
import { postSwrFetcherWithFile } from '../../../utils/post-swr-fetcher-with-file';
import { DocumentDetails } from '../../payments/payment-flow-storage';
import { getBeneficiaryName } from '../../payments/utils/get-beneficiary-name';
import { useEmployee } from '../hooks/use-employee';
import { usePayroll } from '../hooks/use-payroll';
import { usePayrollRecord } from '../hooks/use-payroll-record';
import { PayrollAction, usePayrollFlow } from '../use-payroll-flow';
import { payrollStorageHelpers } from '../utils/payroll-storage';
import { EmployeeDrawer } from './add-employees-step/employee-drawer/employee-drawer';
import { EmployeesTable } from './add-employees-step/employees-table';
import { EmptyState } from './add-employees-step/empty-state';
import { CompletedPayrollSummary } from './completed-payroll-summary/completed-payroll-summary';
import { FinancialInformationForm, PayrollFormData } from './financial-information-step/financial-information-form';
import { EditEmployeeDetailsDrawer } from './payroll-approval-step/edit-employee-drawer/edit-employee-details-drawer';
import { PayrollApprovalWrapper } from './payroll-approval-step/payroll-approval-wrapper';
import { PayrollDraftWrapper } from './payroll-draft-step/payroll-draft-wrapper';
import { PayrollFlowContentWrapper } from './payroll-flow-content-wrapper';
import { BulkPayrollPaymentForm } from './payroll-payment-flow/payroll-payment-form';
import { SuccessStep } from './payroll-payment-flow/success-step';
import { PayrollSetupForm } from './payroll-setup/payroll-setup-form';
import { BeneficiariesList } from './select-beneficiaries/beneficiaries-list';
import { UploadDocumentWrapper } from './upload-document-step/upload-document-wrapper';

const POLLING_INTERVAL = 2000;

type UploadPayrollRequest = { companyId: string; file: File };

type UploadPayrollResponse = {
  evidence: PayrollEvidenceView;
  document: DocumentData;
};

type DocumentData = { id: string; fileName: string; url: string };

type PayrollFlowModalProps = {
  isOpen: boolean;
  payrollAction: PayrollAction;
  legalEntityCountry: LegalEntityCountry;
  companyId: string;
  className?: string;
  beneficiaries: BeneficiaryView[];
  employees: EmployeeView[];
  isLoading: boolean;
  accounts: AccountBankingTypeView[];
  payrolls: PayrollView[];
  onSetPayrollAction: (action: PayrollAction) => void;
  onClose: () => void;
  payrollFlow: ReturnType<typeof usePayrollFlow>;
};

export const PayrollFlowModal = ({
  isOpen,
  payrollAction,
  className,
  legalEntityCountry,
  companyId,
  beneficiaries,
  employees,
  isLoading,
  accounts,
  onSetPayrollAction,
  onClose,
  payrollFlow,
  payrolls,
}: PayrollFlowModalProps) => {
  const modalContentRef = useRef<HTMLDivElement>(null);

  const scrollToTop = () => {
    if (modalContentRef.current) {
      modalContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <Modal
      open={isOpen}
      onOpenChange={() => {
        onClose();
      }}
    >
      <ModalContent className={className} ref={modalContentRef}>
        <VisuallyHiddenPrimitive.Root asChild>
          <div>
            <ModalTitle>Payroll Setup</ModalTitle>
            <ModalDescription>Modal to set up and manage payroll</ModalDescription>
          </div>
        </VisuallyHiddenPrimitive.Root>
        <ModalCloseButton onClick={onClose} />

        <PayrollFlowContent
          payrollAction={payrollAction}
          legalEntityCountry={legalEntityCountry}
          companyId={companyId}
          beneficiaries={beneficiaries}
          employees={employees}
          isLoading={isLoading}
          payrolls={payrolls}
          accounts={accounts}
          onGoBack={onClose}
          scrollToTop={scrollToTop}
          onSetPayrollAction={onSetPayrollAction}
          payrollFlow={payrollFlow}
        />
      </ModalContent>
    </Modal>
  );
};

type PayrollFlowContentProps = {
  payrollAction: PayrollAction;
  legalEntityCountry: LegalEntityCountry;
  companyId: string;
  beneficiaries: BeneficiaryView[];
  employees: EmployeeView[];
  isLoading: boolean;
  payrolls: PayrollView[];
  accounts: AccountBankingTypeView[];
  onGoBack: () => void;
  scrollToTop: () => void;
  onSetPayrollAction: ComponentProps<typeof PayrollFlowModal>['onSetPayrollAction'];
  payrollFlow: ReturnType<typeof usePayrollFlow>;
};

const PayrollFlowContent = ({
  payrollAction,
  legalEntityCountry,
  companyId,
  beneficiaries,
  employees,
  isLoading,
  payrolls,
  accounts,
  onGoBack,
  scrollToTop,
  onSetPayrollAction,
  payrollFlow,
}: PayrollFlowContentProps) => {
  const currency = legalEntityCountry === LegalEntityCountry.GB ? Currency.GBP : Currency.EUR;
  const { isEmployeeDrawerOpen, employeeDrawerMode, selectedEmployeeId, openEmployeeDrawer, closeEmployeeDrawer } =
    payrollFlow;

  const { mutations } = useData();
  const { handleCreateEmployee, handleUpdateEmployee, handleCreateEmployeesFromBeneficiaries } = useEmployee();
  const { isLoading: isPayrollLoading, handleCreatePayroll, handleUpdatePayroll, handlePayrollPayment } = usePayroll();
  const {
    isLoading: isLoadingAddingPayrollRecords,
    addPayrollRecords,
    updatePayrollRecords,
    deletePayrollRecords,
  } = usePayrollRecord();
  const {
    mutations: { triggerVerifyPayee, triggerUpdateEmployee },
  } = usePayeeCheck();
  const [payeeCheckPayrollRecordId, setPayeeCheckPayrollRecordId] = useState<string | null>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { isOcrPayrollLive, isUnifiedPayrollLive } = useFlags();

  const [uploadedEvidenceId, setUploadedEvidenceId] = useState<string | null>(null);

  const [evidenceLineItems, setEvidenceLineItems] = useState<PayrollEvidenceLineItemView[]>([]);

  const { trigger: triggerUploadPayroll, isMutating: isUploadingPayroll } = useSWRMutation<
    UploadPayrollResponse,
    Error,
    string,
    UploadPayrollRequest
  >(`/api/payrolls/${payrollFlow.currentPayroll?.id}/evidence`, postSwrFetcherWithFile);

  useSWR<PayrollEvidenceView, Error, string | null>(
    uploadedEvidenceId ? `/api/payrolls/${payrollFlow.currentPayroll?.id}/evidence/${uploadedEvidenceId}` : null,
    getSwrFetcher,
    {
      refreshInterval: (data) => {
        if (!data) {
          return POLLING_INTERVAL;
        }

        const isEvidenceFinalised = data.status === EvidenceStatus.PROCESSED || data.status === EvidenceStatus.FAILED;

        return isEvidenceFinalised ? 0 : POLLING_INTERVAL;
      },
      refreshWhenHidden: true,
      onSuccess: async (data) => {
        if (isUploadingPayroll) return;
        if (hasProcessedEvidence) return;
        if (data.status === EvidenceStatus.PROCESSED) {
          setHasProcessedEvidence(true);
          setEvidenceLineItems(data.lineItems);
        }
      },
    },
  );

  const [documentDetails, setDocumentDetails] = useState<DocumentDetails | null>(null);

  const [hasProcessedEvidence, setHasProcessedEvidence] = useState(false);

  const onUploadPayroll = async (files: File[]) => {
    setEvidenceLineItems([]);
    setHasProcessedEvidence(false);
    try {
      const response = await triggerUploadPayroll({
        companyId,
        file: files[0],
      });
      if (response.evidence?.id) {
        setDocumentDetails({
          fileName: response.document.fileName,
          documentUrl: response.document.url,
        });
        setUploadedEvidenceId(response.evidence.id);
        onSetPayrollAction('UPLOAD_DOCUMENT');
      } else {
        throw new Error('Missing document details');
      }
    } catch (error) {
      Sentry.addBreadcrumb({
        data: {
          legalEntityCountry,
          companyId,
          payrollId: payrollFlow.currentPayroll?.id,
          files,
          error,
          message: 'triggerUploadPayroll failed',
        },
      });
      Sentry.captureException(error);
      toast.error('Failed to upload document');
    }
  };

  const handleGoBackFromFinancialInformation = async () => {
    if (payrollFlow.currentPayroll?.id) {
      try {
        const payroll = payrollFlow.currentPayroll;
        await deletePayrollRecords(payroll?.id ?? '', payroll?.records.map((record) => record.id) ?? []);
        onSetPayrollAction('MANUAL_ADD_EMPLOYEES');
      } catch {
        toast.error('Error deleting payroll records, please try again.');
      }
    } else {
      Sentry.captureMessage('No payroll ID found when trying to go back from financial information');
      toast.error('Error deleting payroll records, please try again.');
    }
  };

  const firstEligibleModulrAccount = accounts.find(
    (account) => isModulrAccount(account) && account.currency === currency,
  );

  switch (payrollAction) {
    case 'PAYROLL_SETUP': {
      const payroll = payrollFlow.currentPayroll;
      const previousPayroll = getSortedData({
        payrolls: payrolls.filter((payroll) => payroll.status === PayrollStatus.SUCCEEDED),
        organisations: [],
        companies: [],
        accounts: [],
        transactions: [],
        vendors: [],
        categories: [],
        evidence: [],
        beneficiaries: [],
        documents: [],
        employees: [],
        payrollRecords: [],
        cards: [],
      }).payrolls[0];
      const defaultValues = {
        country: payroll?.country,
        currency: payroll?.currency,
        payrollName: payroll?.name,
        payrollTimePeriod: payroll?.period,
        payrollReference: payroll?.reference,
      };

      return (
        <PayrollFlowContentWrapper
          title="Payroll Setup"
          onGoBack={onGoBack}
          isFullScreenWidth={true}
          currentAction={payrollAction}
        >
          <div className="mx-auto w-[520px]">
            <PayrollSetupForm
              copiedPayrollId={payrollFlow.copiedPayroll?.id}
              previousPayroll={previousPayroll}
              defaultValues={payrollFlow.currentPayroll ? defaultValues : undefined}
              legalEntityCountry={legalEntityCountry}
              onClick={async (data, copyFromPayrollId) => {
                try {
                  payrollFlow.setSelectedRowIds([]);
                  if (payrollFlow.currentPayroll) {
                    await handleUpdatePayroll(payrollFlow.currentPayroll.id, data);

                    onSetPayrollAction(employees.length > 0 ? 'MANUAL_ADD_EMPLOYEES' : 'MANUAL_SELECT_BENEFICIARIES');
                  } else if (copyFromPayrollId || payrollFlow.copiedPayroll) {
                    const payroll = await handleCreatePayroll({
                      ...data,
                      copiedFromPayrollId: copyFromPayrollId || payrollFlow.copiedPayroll?.id,
                    });
                    payrollFlow.setCopiedPayroll(null);
                    payrollFlow.setCurrentPayroll(payroll);

                    onSetPayrollAction('FINANCIAL_INFORMATION');
                  } else {
                    const payroll = await handleCreatePayroll(data);
                    payrollFlow.setCurrentPayroll(payroll);

                    onSetPayrollAction(employees.length > 0 ? 'MANUAL_ADD_EMPLOYEES' : 'MANUAL_SELECT_BENEFICIARIES');
                  }
                } catch {
                  toast.error('Error saving payroll');
                }
              }}
              isLoading={isPayrollLoading}
            />
          </div>
        </PayrollFlowContentWrapper>
      );
    }
    case 'MANUAL_SELECT_BENEFICIARIES': {
      return (
        <PayrollFlowContentWrapper
          title="Select Employees"
          onGoBack={() => onSetPayrollAction('PAYROLL_SETUP')}
          currentAction={payrollAction}
        >
          <Stack space={6} className="w-full" justify="between">
            <p className="text-sm text-gray-500">
              Select the employees you want to add to your payroll. You can search by name or email.
            </p>
            <BeneficiariesList
              beneficiaries={beneficiaries}
              payrollCurrency={payrollFlow.currentPayroll?.currency ?? Currency.EUR}
              handleCreateEmployeesFromBeneficiaries={handleCreateEmployeesFromBeneficiaries}
              onSetPayrollAction={onSetPayrollAction}
              onManuallyAddEmployees={() => {
                openEmployeeDrawer('ADD');
                onSetPayrollAction('MANUAL_ADD_EMPLOYEES');
              }}
              isLoading={isLoading}
            />
          </Stack>
          {isOcrPayrollLive && (
            <FileDropZone
              variant="INLINE"
              onDropAccepted={onUploadPayroll}
              isLoading={isUploadingPayroll}
              fileDescription="payroll"
              supportedFileTypes={['PDF', 'CSV']}
            />
          )}
        </PayrollFlowContentWrapper>
      );
    }
    case 'UPLOAD_DOCUMENT': {
      const currentPayroll = payrollFlow.currentPayroll as PayrollView;
      return (
        <UploadDocumentWrapper
          isLoadingEmployees={isLoadingAddingPayrollRecords}
          isSubmittingNewEmployee={isSubmitting}
          legalEntityCountry={legalEntityCountry}
          companyId={companyId}
          handleCreateEmployee={handleCreateEmployee}
          accounts={accounts}
          employees={employees}
          onGoBack={onGoBack}
          scrollToTop={scrollToTop}
          documentDetails={documentDetails}
          payrollFlow={payrollFlow}
          evidenceLineItems={evidenceLineItems}
          onSetPayrollAction={onSetPayrollAction}
          updatePayrollRecords={updatePayrollRecords}
          addPayrollRecords={addPayrollRecords}
          deletePayrollRecords={deletePayrollRecords}
          handleUpdatePayroll={handleUpdatePayroll}
          currentPayroll={currentPayroll}
          currentAction={payrollAction}
        />
      );
    }
    case 'MANUAL_ADD_EMPLOYEES': {
      if (isUnifiedPayrollLive) {
        onSetPayrollAction('PAYROLL_DRAFT');
        return <LoadingSection text="" />;
      }

      return (
        <PayrollFlowContentWrapper
          isFullScreenWidth={true}
          onGoBack={() =>
            employees.length > 0
              ? onSetPayrollAction('PAYROLL_SETUP')
              : onSetPayrollAction('MANUAL_SELECT_BENEFICIARIES')
          }
          currentAction={payrollAction}
        >
          {employees.length > 0 ? (
            <EmployeesTable
              employees={employees}
              onEditEmployee={(employeeId) => openEmployeeDrawer('EDIT', employeeId)}
              onAddEmployee={() => openEmployeeDrawer('ADD')}
              onContinue={() => {
                onSetPayrollAction('FINANCIAL_INFORMATION');
              }}
              selectedRowIds={payrollFlow.selectedRowIds}
              setSelectedRowIds={payrollFlow.setSelectedRowIds}
              payrollId={payrollFlow.currentPayroll?.id}
              addPayrollRecords={addPayrollRecords}
              isLoading={isLoadingAddingPayrollRecords}
            />
          ) : (
            <EmptyState onAddEmployee={() => openEmployeeDrawer('ADD')} />
          )}
          {isOcrPayrollLive && (
            <div className="mx-auto flex justify-center">
              <FileDropZone
                variant="INLINE"
                onDropAccepted={onUploadPayroll}
                isLoading={isUploadingPayroll}
                fileDescription="payroll"
                supportedFileTypes={['PDF', 'CSV']}
              />
            </div>
          )}
          <EmployeeDrawer
            employee={employees.find((employee) => employee.id === selectedEmployeeId)}
            beneficiaries={beneficiaries}
            isOpen={isEmployeeDrawerOpen}
            onClose={closeEmployeeDrawer}
            mode={employeeDrawerMode}
            selectedEmployeeId={selectedEmployeeId}
            legalEntityCountry={legalEntityCountry}
            companyId={companyId}
            onSave={(formData) => {
              if (employeeDrawerMode === 'ADD') {
                handleCreateEmployee(formData);
              } else if (selectedEmployeeId && employeeDrawerMode === 'EDIT') {
                handleUpdateEmployee(selectedEmployeeId, formData);
              }
              closeEmployeeDrawer();
            }}
            isSubmitting={isLoading}
          />
        </PayrollFlowContentWrapper>
      );
    }
    case 'FINANCIAL_INFORMATION': {
      if (isUnifiedPayrollLive) {
        onSetPayrollAction('PAYROLL_DRAFT');
        return <LoadingSection text="" />;
      }

      const payroll = payrollFlow.currentPayroll;
      const employeesOnPayroll = employees.filter((employee) =>
        payroll?.records.some((record) => record.employeeId === employee.id),
      );

      const onContinue = async () => {
        try {
          if (!payrollFlow.currentPayroll?.id) {
            return;
          }
          await handleUpdatePayroll(payrollFlow.currentPayroll.id, {
            status: PayrollStatus.WAITING_FOR_APPROVAL,
          });
          onSetPayrollAction('PAYROLL_SUMMARY');
        } catch (error) {
          Sentry.captureException(error, {
            extra: {
              payrollId: payrollFlow.currentPayroll?.id,
            },
          });
          toast.error('Error updating payroll');
        }
      };

      return (
        <PayrollFlowContentWrapper
          onGoBack={handleGoBackFromFinancialInformation}
          isFullScreenWidth={true}
          currentAction={payrollAction}
        >
          <div className="mx-auto">
            <FinancialInformationForm
              employees={employeesOnPayroll}
              currency={currency}
              payrollReference={payroll?.reference}
              isLoading={isLoadingAddingPayrollRecords || isSubmitting}
              onSubmit={async (data: PayrollFormData) => {
                const payrollId = payrollFlow.currentPayroll?.id;
                const currentPayroll = payroll;

                setIsSubmitting(true);
                try {
                  if (payrollId && currentPayroll?.records) {
                    const sourceAccountConfig =
                      firstEligibleModulrAccount && firstEligibleModulrAccount.fspAdapter
                        ? {
                            isPresent: true as const,
                            id: firstEligibleModulrAccount.id,
                            fspAdapter: firstEligibleModulrAccount.fspAdapter,
                          }
                        : {
                            isPresent: false as const,
                          };
                    await updatePayrollRecords(
                      payrollId,
                      currentPayroll.records,
                      data.employees,
                      sourceAccountConfig,
                      currency,
                    );
                    onContinue();
                  } else {
                    const errorMessage = currentPayroll?.records
                      ? 'No payroll ID provided'
                      : 'No payroll records found';
                    Sentry.captureMessage(errorMessage, {
                      level: 'error',
                      extra: {
                        payrollId,
                        employeesCount: data.employees.length,
                        hasPayrollReference: !!payroll?.reference,
                        hasCurrentPayroll: !!currentPayroll,
                        hasPayrollRecords: !!currentPayroll?.records,
                      },
                      tags: {
                        component: 'FinancialInformationForm',
                        action: 'onSubmitForm',
                        issue: currentPayroll?.records ? 'missing_payroll_id' : 'missing_payroll_records',
                      },
                    });
                    toast.error('Invalid payroll configuration');
                  }
                } catch (error) {
                  Sentry.captureException(error, {
                    extra: {
                      payrollId,
                      employeesCount: data.employees.length,
                      payrollReference: payroll?.reference,
                      totalNetPay: data.employees.reduce((acc, emp) => acc + Number(emp.netPay || 0), 0),
                    },
                    tags: {
                      component: 'FinancialInformationForm',
                      action: 'onSubmitForm',
                    },
                  });
                  toast.error('Failed to process payroll records');
                } finally {
                  setIsSubmitting(false);
                }
              }}
              currentPayroll={payroll}
            />
          </div>
          {isOcrPayrollLive && (
            <div className="mx-auto flex justify-center">
              <FileDropZone
                variant="INLINE"
                onDropAccepted={onUploadPayroll}
                isLoading={isUploadingPayroll}
                fileDescription="payroll"
                supportedFileTypes={['PDF', 'CSV']}
              />
            </div>
          )}
        </PayrollFlowContentWrapper>
      );
    }
    case 'PAYROLL_DRAFT': {
      const payroll = payrollFlow.currentPayroll;

      return (
        <PayrollFlowContentWrapper
          title="Payroll Draft"
          isFullScreenWidth={true}
          currentAction={payrollAction}
          onGoBack={() =>
            employees.length > 0
              ? onSetPayrollAction('PAYROLL_SETUP')
              : onSetPayrollAction('MANUAL_SELECT_BENEFICIARIES')
          }
        >
          {employees.length > 0 ? (
            <PayrollDraftWrapper
              payrollFlow={payrollFlow}
              currentPayroll={payroll}
              employees={employees}
              currency={currency}
              accounts={accounts}
              deletePayrollRecords={deletePayrollRecords}
              addPayrollRecords={addPayrollRecords}
              updatePayrollRecords={updatePayrollRecords}
              onContinue={async () => {
                await handleUpdatePayroll(payroll?.id || '', {
                  status: PayrollStatus.WAITING_FOR_APPROVAL,
                });
                onSetPayrollAction('PAYROLL_SUMMARY');
              }}
              openEmployeeDrawer={openEmployeeDrawer}
            />
          ) : (
            <EmptyState onAddEmployee={() => openEmployeeDrawer('ADD')} />
          )}
          {isOcrPayrollLive && (
            <div className="mx-auto flex justify-center">
              <FileDropZone
                variant="INLINE"
                onDropAccepted={onUploadPayroll}
                isLoading={isUploadingPayroll}
                fileDescription="payroll"
                supportedFileTypes={['PDF', 'CSV']}
              />
            </div>
          )}
          <EmployeeDrawer
            employee={employees.find((employee) => employee.id === selectedEmployeeId)}
            isOpen={isEmployeeDrawerOpen}
            onClose={closeEmployeeDrawer}
            mode={employeeDrawerMode}
            selectedEmployeeId={selectedEmployeeId}
            legalEntityCountry={legalEntityCountry}
            companyId={companyId}
            onSave={(formData) => {
              if (employeeDrawerMode === 'ADD') {
                handleCreateEmployee(formData);
              } else if (selectedEmployeeId && employeeDrawerMode === 'EDIT') {
                handleUpdateEmployee(selectedEmployeeId, formData);
              }
              closeEmployeeDrawer();
            }}
            isSubmitting={isLoading}
          />
        </PayrollFlowContentWrapper>
      );
    }
    case 'PAYROLL_SUMMARY': {
      if (!payrollFlow.currentPayroll || !firstEligibleModulrAccount?.fspAdapter) {
        return null;
      }

      const payroll = payrollFlow.currentPayroll;

      if (!payroll) {
        return null;
      }

      const payrollRecord = payroll.records.find((record) => record.id === payeeCheckPayrollRecordId);
      const selectedEmployee = employees.find((employee) => employee.id === payrollRecord?.employeeId);

      return (
        <>
          <PayrollFlowContentWrapper
            onGoBack={() => {
              handleUpdatePayroll(payroll.id, { ...payroll, status: PayrollStatus.IN_DRAFT });
              onSetPayrollAction('FINANCIAL_INFORMATION');
              payrollStorageHelpers.clearAllPayrollStorage(payroll.id);
            }}
            isFullScreenWidth={true}
            currentAction={payrollAction}
          >
            <PayrollApprovalWrapper
              payroll={payroll}
              currency={currency}
              isVopRequired={isVopCheckRequired({
                currency,
                fspAdapter: firstEligibleModulrAccount.fspAdapter,
              })}
              isLoading={isPayrollLoading}
              onContinue={async () => {
                if (payrollFlow.currentPayroll?.id) {
                  await handleUpdatePayroll(payrollFlow.currentPayroll?.id, {
                    ...payrollFlow.currentPayroll,
                    currency,
                    status: PayrollStatus.APPROVED,
                  });
                  onSetPayrollAction('REVIEW_AND_PAY');
                }
              }}
              onEditPayrollRecord={(payrollRecordId) => {
                setPayeeCheckPayrollRecordId(payrollRecordId);
              }}
            />
          </PayrollFlowContentWrapper>

          {selectedEmployee &&
            isVopPayeeCheck(selectedEmployee.latestPayeeCheck) &&
            selectedEmployee.latestPayeeCheck?.code && (
              <EditEmployeeDetailsDrawer
                isOpen={!!payeeCheckPayrollRecordId}
                onClose={() => setPayeeCheckPayrollRecordId(null)}
                employeeDetailsConfig={{
                  firstName: selectedEmployee.firstName,
                  lastName: selectedEmployee.lastName,
                  employeeId: selectedEmployee.employeeId,
                }}
                bankAccountDetailsConfig={{
                  employeeDetails: {
                    fname: selectedEmployee.firstName,
                    lname: selectedEmployee.lastName,
                    iban: selectedEmployee.destination.iban,
                  },
                  matchedName: selectedEmployee.latestPayeeCheck?.matchedName,
                  isCurrencyGbp: currency === Currency.GBP,
                  isVerifyingPayee: isSubmitting,
                  payeeCheckCode: selectedEmployee.latestPayeeCheck.code,
                  onEditEmployee: async (data) => {
                    if (!firstEligibleModulrAccount) {
                      Sentry.captureMessage(
                        'No eligible Modulr account found when calling onEditEmployee in EditEmployeeDetailsDrawer',
                        {
                          extra: {
                            employees,
                            accounts,
                            currency,
                          },
                        },
                      );

                      return;
                    }

                    try {
                      setIsSubmitting(true);

                      await triggerUpdateEmployee({
                        urlSuffix: `/${selectedEmployee.id}`,
                        firstName: data.fname,
                        lastName: data.lname,
                        destination: {
                          iban: data.iban,
                        },
                      });

                      await triggerVerifyPayee({
                        paymentAccountId: firstEligibleModulrAccount.id,
                        employeeId: selectedEmployee.id,
                        name: getBeneficiaryName({
                          firstName: data.fname,
                          lastName: data.lname,
                        }),
                        iban: data.iban,
                      });

                      await mutations.handleIncrementalUpdates();

                      setPayeeCheckPayrollRecordId(null);

                      toast.success('Employee updated successfully');
                    } catch (error) {
                      Sentry.captureException(error);

                      toast.error('Failed to edit employee');
                    } finally {
                      setIsSubmitting(false);
                    }
                  },
                  onFinalizeFlow: () => setPayeeCheckPayrollRecordId(null),
                }}
              />
            )}
        </>
      );
    }
    case 'REVIEW_AND_PAY': {
      if (!payrollFlow.currentPayroll) {
        return null;
      }

      const payroll = payrollFlow.currentPayroll;

      if (!payroll) {
        return null;
      }
      return (
        <PayrollFlowContentWrapper onGoBack={onGoBack} currentAction={payrollAction}>
          <BulkPayrollPaymentForm
            payroll={payroll}
            accounts={accounts}
            isLoading={isPayrollLoading}
            onSubmit={async (paymentDetails) => {
              const response = await handlePayrollPayment(payroll.id, paymentDetails);
              if (response.authorisationUrl) {
                window.location.href = response.authorisationUrl;
              }
            }}
          />
        </PayrollFlowContentWrapper>
      );
    }
    case 'PAYROLL_PAYMENT_SUCCESS': {
      const payroll = payrollFlow.currentPayroll;

      if (!payroll) {
        return null;
      }

      const totalNetPay = payroll.records.reduce((acc, record) => acc + record.netPay, 0);
      const currencySymbol = currency === Currency.GBP ? '£' : '€';

      return (
        <PayrollFlowContentWrapper
          title="Payment Successful"
          onGoBack={onGoBack}
          isFullScreenWidth={true}
          currentAction={payrollAction}
        >
          <div className="mx-auto mt-10 flex w-[520px] justify-center">
            <SuccessStep
              payrollName={payroll.name}
              employeeCount={payroll.records.length}
              amount={`${currencySymbol}${totalNetPay.toFixed(2)}`}
              onMakeNewPayroll={() => {
                payrollFlow.clearPayrollState();
                onSetPayrollAction('PAYROLL_SETUP');
              }}
              onClose={() => {
                payrollFlow.clearPayrollState();
                onGoBack();
              }}
            />
          </div>
        </PayrollFlowContentWrapper>
      );
    }
    case 'COMPLETED_PAYROLL_SUMMARY': {
      return (
        <PayrollFlowContentWrapper
          title={`${payrollFlow.currentPayroll?.name}`}
          onGoBack={onGoBack}
          isFullScreenWidth={true}
          hideSidebar={true}
          currentAction={payrollAction}
        >
          <div className="mx-auto mt-10 flex justify-center">
            <CompletedPayrollSummary
              payroll={payrollFlow.currentPayroll}
              currency={currency}
              onContinue={() => {
                const copiedPayroll = payrollFlow.currentPayroll;
                payrollFlow.clearPayrollState();
                payrollFlow.setCopiedPayroll(copiedPayroll);
                onSetPayrollAction('PAYROLL_SETUP');
              }}
            />
          </div>
        </PayrollFlowContentWrapper>
      );
    }
    default:
      return exhaustiveCheck(payrollAction);
  }
};
